// bully ignore[directory-kebab-case: component-directory-required requires this directory to literally equal the PascalCase file stem (QuickCreateCards), and no rule enforces PascalCase .tsx filenames — kebab-casing both dir and file instead trips oxlint's unicorn/filename-case; no shape satisfies all three checks at once. follow-up: revisit if bully's directory-kebab-case gains a component-directory exemption.]
import AddOutlined from '@mui/icons-material/AddOutlined'
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined'
import React from 'react'
import styled from 'styled-components'
import type { DropdownMenuEvent, DropdownMenuItem } from 'sutra/components'
import { DropdownMenu } from 'sutra/components'
import { accents, brand } from 'sutra/tokens/colors'
import { radii } from 'sutra/tokens/radii'
import { spacing } from 'sutra/tokens/spacing'

import { DetectedIssueIcon } from 'features/trend-detection/components/IssueShared'
import { GapMeasurementIcon } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'

const ICON_COLOR = '#28364F'
// Sized per each icon's native viewBox (issue 22:16, benchmarking 16:16)
// so a shared width/height doesn't squash the benchmarking strokes.
const ISSUE_ICON_WIDTH = 18
const ISSUE_ICON_HEIGHT = 13
const BENCHMARKING_ICON_SIZE = 16

export type QuickCreateAdditionalType = {
  key: string
  label: string
  onClick: () => void
}

export type QuickCreateCardsProps = {
  onCreateIssueAgent: () => void
  onCreateBenchmarkingAgent: () => void
  /** Extra agent types surfaced behind a discreet chevron dropdown. When empty or omitted, the chevron is not rendered. */
  additionalTypes?: QuickCreateAdditionalType[]
}

export const QuickCreateCards: React.FC<QuickCreateCardsProps> = ({
  onCreateIssueAgent,
  onCreateBenchmarkingAgent,
  additionalTypes,
}) => {
  const hasAdditional = !!additionalTypes && additionalTypes.length > 0
  const menuItems: DropdownMenuItem[] = hasAdditional
    ? additionalTypes.map((t) => ({ key: t.key, label: t.label }))
    : []
  const handleMenuAction = (key: string, event: DropdownMenuEvent): void => {
    event.stopPropagation()
    const picked = additionalTypes?.find((t) => t.key === key)
    picked?.onClick()
  }

  return (
    <Row>
      <QuickCreateCard
        type="button"
        onClick={onCreateIssueAgent}
        aria-label="Create Issue Agent"
      >
        <LeftBlock>
          <TitleRow>
            <DetectedIssueIcon
              color={ICON_COLOR}
              width={ISSUE_ICON_WIDTH}
              height={ISSUE_ICON_HEIGHT}
            />
            <Title>Issue Agent</Title>
          </TitleRow>
          <Subtitle>Monitor feedback for emerging issues</Subtitle>
        </LeftBlock>
        <PlusTile aria-hidden="true">
          <AddOutlined fontSize="small" />
        </PlusTile>
      </QuickCreateCard>

      <QuickCreateCard
        type="button"
        onClick={onCreateBenchmarkingAgent}
        aria-label="Create Benchmarking Agent"
      >
        <LeftBlock>
          <TitleRow>
            <GapMeasurementIcon
              direction="leading"
              color={ICON_COLOR}
              width={BENCHMARKING_ICON_SIZE}
              height={BENCHMARKING_ICON_SIZE}
            />
            <Title>Benchmarking Agent</Title>
          </TitleRow>
          <Subtitle>See where your brand leads or lags</Subtitle>
        </LeftBlock>
        <PlusTile aria-hidden="true">
          <AddOutlined fontSize="small" />
        </PlusTile>
      </QuickCreateCard>

      {hasAdditional ? (
        <DropdownMenu
          side="bottom"
          align="end"
          items={menuItems}
          onAction={handleMenuAction}
        >
          <MoreTypesButton type="button" aria-label="More agent types">
            <KeyboardArrowDownOutlined fontSize="small" />
          </MoreTypesButton>
        </DropdownMenu>
      ) : null}
    </Row>
  )
}

const Row = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${spacing[4]}px;
  flex-wrap: nowrap;
`

const MoreTypesButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 74px;
  border: 1px solid #e9eaeb;
  border-radius: 9px;
  background: ${brand.white};
  color: rgba(40, 54, 79, 0.6);
  cursor: pointer;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${accents.reflectionBlue[500]};
    color: ${brand.stillNight[800]};
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const QuickCreateCard = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing[3]}px;
  padding: ${spacing[5]}px ${spacing[4]}px;
  width: 322px;
  flex-shrink: 0;
  background: ${brand.white};
  border: 1px solid #e9eaeb;
  border-radius: 9px;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: border-color 120ms ease;

  &:hover {
    border-color: ${accents.reflectionBlue[500]};
  }

  &:focus-visible {
    outline: 2px solid ${accents.reflectionBlue[500]};
    outline-offset: 2px;
  }
`

const LeftBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 20px;
`

const Title = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${brand.stillNight[800]};
  line-height: 20px;
`

const Subtitle = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: rgba(40, 54, 79, 0.6);
  line-height: 18px;
`

const PlusTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${radii.md}px;
  background: ${accents.reflectionBlue[500]};
  color: ${brand.white};
  flex-shrink: 0;
`
